/* ---- Haptic feedback on selection ---------------------------------------

   Two platforms, two completely different mechanisms, one API for the rest
   of the theme: window.Trulvl.haptic().

   ANDROID -- navigator.vibrate(). The real Vibration API. Well supported in
   Chrome/Android for years. A single short pulse; anything longer than
   ~10ms stops reading as a tap confirmation and starts reading as an alert.

   iOS -- navigator.vibrate() does not exist and never has. Safari has never
   shipped the Vibration API, and it is not coming via a flag or a
   permission prompt. The only route from a web page is a side effect: since
   iOS 17.4, toggling a native switch control (<input type="checkbox"
   switch>) plays the system's selection haptic. Driving that control
   off-screen produces the tick without showing a switch.

   That is a trick, not an API, and it is worth being honest about what that
   means: Apple can change it in any Safari release, in which case iOS users
   silently get no haptic. Nothing else breaks -- there is no error, no
   layout impact, no blocked interaction. That is the whole risk, and it is
   why this ships behind an admin toggle rather than as an assumption.

   WHAT WE DO NOT DO:
   - No haptic on scroll, hover, or page load. Feedback belongs on a
     deliberate selection; anywhere else it reads as a malfunction.
   - No haptic on desktop, even with a touchscreen -- it fires on
     (pointer: coarse) only, so a trackpad tap never triggers it.
   - Respects prefers-reduced-motion. Vestibular triggers aside, a user who
     has asked their OS for less motion has not asked for buzzing.
--------------------------------------------------------------------------*/
(function () {
  "use strict";

  var root = document.documentElement;
  if (root.getAttribute("data-haptics") === "false") return;

  // Coarse pointer only. A desktop with a touchscreen still reports
  // (pointer: fine) for its primary pointer, which is the behaviour we want.
  var isTouch = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  if (!isTouch) return;

  var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return;

  var canVibrate = typeof navigator.vibrate === "function";
  var iosSwitch = null;

  /* Built lazily and only on iOS, so Android never pays for markup it
     cannot use. aria-hidden + tabindex -1 + inert-by-position: it must not
     be reachable, announced, or focusable -- it is not a control, it is a
     noisemaker. */
  function getIosSwitch() {
    if (iosSwitch) return iosSwitch;
    var el = document.createElement("input");
    el.type = "checkbox";
    el.setAttribute("switch", "");
    el.setAttribute("aria-hidden", "true");
    el.tabIndex = -1;
    el.style.cssText =
      "position:fixed;left:-9999px;top:0;width:1px;height:1px;" +
      "opacity:0;pointer-events:none;margin:0;padding:0;border:0;";
    document.body.appendChild(el);
    iosSwitch = el;
    return el;
  }

  // Feature-detect the switch attribute rather than sniffing the UA: if the
  // browser doesn't understand it, this is a plain checkbox and toggling it
  // does nothing at all -- harmless, just silent.
  var supportsSwitch = (function () {
    var t = document.createElement("input");
    t.type = "checkbox";
    t.setAttribute("switch", "");
    return t.matches && (function () {
      try { return t.matches("input[switch]"); } catch (e) { return false; }
    })();
  })();

  var last = 0;

  function haptic() {
    // Rate-limit. Two taps inside 60ms is a double-fire, not two decisions,
    // and back-to-back pulses feel like a stutter rather than confirmation.
    var now = Date.now();
    if (now - last < 60) return;
    last = now;

    if (canVibrate) {
      try { navigator.vibrate(8); } catch (e) { /* no-op */ }
      return;
    }
    if (supportsSwitch) {
      try {
        var el = getIosSwitch();
        el.checked = !el.checked;
      } catch (e) { /* no-op */ }
    }
  }

  window.Trulvl = window.Trulvl || {};
  window.Trulvl.haptic = haptic;

  /* Bound to the same pointerdown moment the visual selection uses, so the
     tick and the color land together. Delegated at the document so it
     covers markup added later (cart drawer contents, predictive search
     results) without rebinding.

     The selector list is deliberate rather than "every link": a haptic on
     every tap anywhere is noise. These are the deliberate selections --
     navigation, category choice, size/color, and the buttons that change
     the cart. */
  var SELECTORS = [
    ".site-header__mobile-panel nav a",
    ".site-header__mobile-social a",
    ".site-header__mobile-feature",
    ".collection-tile:not(.is-disabled)",
    ".option-swatch:not(.is-sold-out):not([disabled])",
    ".btn-buy-now",
    "[data-cart-toggle]",
    "[data-menu-toggle]",
    "[data-search-toggle]",
    "[data-wishlist-toggle]",
    ".cart-drawer__qty button",
    ".product-card"
  ].join(",");

  document.addEventListener(
    "pointerdown",
    function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.pointerType === "mouse") return;
      if (e.target.closest(SELECTORS)) haptic();
    },
    { passive: true }
  );
})();
